import { useState } from "react";
import { useNavigate } from "react-router";
import { get, post } from "../api/client";
import { useLoad } from "../api/hooks";
import type { Plan, Plans } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Shell } from "../components/Shell";
import { ErrorLine } from "../components/ui";
import { fmtMoney } from "../lib/format";
import { useIsDesktop } from "../lib/device";

/** 09 · Plans (A): three cards on the phone, the spec-sheet comparison on desktop. Prices follow the billing country. */
export default function PlansPage() {
  const desktop = useIsDesktop();
  const nav = useNavigate();
  const { me, plan: current } = useAuth();
  const [region, setRegion] = useState<"IN" | "GLOBAL" | null>(null);
  const plans = useLoad(() => get<Plans>(`/v1/billing/plans${region ? `?region=${region}` : ""}`), [region]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<Error | null>(null);
  const shown = plans.data?.region ?? "GLOBAL";
  const choose = async (code: "basic" | "pro") => {
    if (!me) return nav("/signin");
    setBusy(code);
    setErr(null);
    try {
      const r = await post<{ checkout_url: string }>("/v1/billing/checkout", { plan_code: code });
      window.location.assign(r.checkout_url);
    } catch (e) {
      setErr(e as Error);
      setBusy(null);
    }
  };
  const priceOf = (p: Plan) => (p.price ? (p.price.amount_minor === 0 ? fmtMoney(0, p.price.currency) : `${fmtMoney(p.price.amount_minor, p.price.currency)} / mo`) : "—");
  const desc = (p: Plan) => {
    const e = p.entitlements;
    if (p.code === "free") return `${e.videos_per_month} matches a month · whole-match clips · kept ${e.retention_days} days`;
    if (p.code === "basic") return `${e.videos_per_month} matches · clips by player · kept ${e.retention_days} days · set-up in a day`;
    return `${e.videos_per_month} matches · ${e.seats} managers · kept ${e.retention_days} days · set-up same day`;
  };
  const cta = (p: Plan) => (p.code === current ? "Current plan" : p.code === "free" ? "Included" : p.code === "basic" ? "Choose Basic" : "Try 14 days free");
  const btnClass = (p: Plan) => (p.code === current || p.code === "free" ? "ghost" : p.code === "pro" ? "dark" : "");
  const regionLine = shown === "IN" ? "Prices in ₹ for cards and UPI issued in India" : "Prices in USD";
  const toggle = <button className="u" onClick={() => setRegion(shown === "IN" ? "GLOBAL" : "IN")} data-testid="region-toggle">{shown === "IN" ? "show USD" : "show ₹"}</button>;
  const rows: [string, (p: Plan) => string][] = [
    ["Matches a month", (p) => String(p.entitlements.videos_per_month)],
    ["Each video up to", (p) => `${Math.round(p.entitlements.max_video_seconds / 3600)} h`],
    ["Clips kept", (p) => `${p.entitlements.retention_days} days`],
    ["Clips by player", (p) => (p.code === "free" ? "—" : "Included")],
    ["Set-up turnaround", (p) => (p.code === "free" ? "3 days" : p.code === "basic" ? "1 day" : "Same day")],
    ["Analysis queue", (p) => (p.code === "free" ? "Idle time" : p.code === "basic" ? "Priority" : "First")],
    ["Team managers", (p) => String(p.entitlements.seats)],
    ["Team players", () => "Unlimited"],
  ];
  const list = plans.data?.plans ?? [];
  return (
    <Shell title="Plans" back={me ? "/" : "/signin"} active="Plans" right={<span />}>
      <div className="p" style={{ paddingTop: desktop ? 48 : 8, paddingBottom: 40 }} data-testid="plans">
        {desktop ? <><h1 className="center">Plans</h1><p className="sub center mt10">{regionLine} · {toggle}</p></> : <p className="tiny">{regionLine} · {toggle}</p>}
        <ErrorLine error={plans.error ?? err} />
        {desktop ? (
          <table className="tbl mt32">
            <thead>
              <tr>
                <th style={{ width: "34%" }} />
                {list.map((p) => (
                  <th className="c" key={p.code}>
                    <div style={{ fontSize: 20 }}>{p.name}</div>
                    <div className="sub" style={{ fontWeight: 400 }}>{priceOf(p)}</div>
                    <div className="mt16"><button className={`btn ${btnClass(p)} sm`} disabled={p.code === current || p.code === "free" || busy !== null} onClick={() => choose(p.code as "basic" | "pro")} data-testid={`choose-${p.code}`}>{cta(p)}</button></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, fn]) => (
                <tr key={label}><td>{label}</td>{list.map((p) => <td className="c" key={p.code}>{fn(p)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        ) : (
          list.map((p) => (
            <div className="card mt16" key={p.code}>
              <div className="row between"><div style={{ fontSize: 20, fontWeight: 500 }}>{p.name}</div><div className="med">{priceOf(p)}</div></div>
              <p className="sub mt6">{desc(p)}</p>
              <div className="mt16"><button className={`btn ${btnClass(p)}`} disabled={p.code === current || p.code === "free" || busy !== null} onClick={() => choose(p.code as "basic" | "pro")} data-testid={`choose-${p.code}`}>{cta(p)}</button></div>
            </div>
          ))
        )}
        <p className={desktop ? "tiny mt24" : "tiny mt16"}>Annual plans: two months free. Cancel any time; clips stay until their expiry date.</p>
      </div>
    </Shell>
  );
}
